import React from "react";

import styles from "./planLimitBanner.module.css";

const formatNumber = (value) => {
  if (typeof value !== "number" || Number.isNaN(value)) return "—";
  return value.toLocaleString("pt-BR");
};

// Plural simples em pt-BR (evita o "(s)" artificial nas mensagens).
const pluralize = (count, singular, plural) =>
  Math.abs(count) === 1 ? singular : plural ?? `${singular}s`;

/**
 * Exibe um banner com o status do plano contratado pela mantenedora.
 * Sempre que o limite estiver excedido (LimitExceeded = true), apresenta um CTA
 * por plano de upgrade disponível e — quando aplicável — para ajustar o arquivo.
 *
 * Props:
 *  - planLimit: objeto PlanLimitInfo retornado pelo backend (ou null).
 *  - variant: "exceeded-only" (default) exibe somente quando excedido;
 *             "always" exibe sempre o status (usado em telas de visão geral).
 *  - onUpgradeClick: callback opcional quando o usuário clica em "Fazer upgrade".
 *                    Sem argumento — a escolha do plano ocorre no passo seguinte.
 *  - onAdjustFileClick: callback opcional quando o usuário clica em "Ajustar arquivo".
 *                      O botão só aparece em fluxos de importação (quando há projeção).
 */
const PlanLimitBanner = ({ planLimit, variant = "exceeded-only", onUpgradeClick, onAdjustFileClick }) => {
  if (!planLimit) return null;

  const limitExceeded = !!planLimit.limitExceeded;

  if (variant === "exceeded-only" && !limitExceeded) {
    return null;
  }

  const current = planLimit.currentActiveStudents;
  const max = planLimit.maxActiveStudents;
  const planName = planLimit.currentPlanName ?? "—";
  const mantenedoraName = planLimit.mantenedoraName;

  // Todas as opções de upgrade disponíveis. Fallback para o "próximo plano"
  // caso o backend (resposta antiga em cache) ainda não traga a lista.
  const upgradePlans =
    Array.isArray(planLimit.availableUpgradePlans) && planLimit.availableUpgradePlans.length > 0
      ? planLimit.availableUpgradePlans
      : planLimit.nextPlanName
      ? [
          {
            id: planLimit.nextPlanId,
            name: planLimit.nextPlanName,
            maxActiveStudents: planLimit.nextPlanMaxActiveStudents
          }
        ]
      : [];
  const hasUpgrade = upgradePlans.length > 0;

  const optionsList = (
    <>
      {upgradePlans.map((p, i) => (
        <React.Fragment key={p.id || p.name}>
          {i > 0 ? (i === upgradePlans.length - 1 ? " ou " : ", ") : ""}
          <strong>{p.name}</strong>
          {p.maxActiveStudents != null && (
            <> (até <strong>{formatNumber(p.maxActiveStudents)}</strong> alunos ativos)</>
          )}
        </React.Fragment>
      ))}
    </>
  );

  const handleUpgrade = () => {
    if (typeof onUpgradeClick === "function") {
      onUpgradeClick();
      return;
    }
    console.info("[PlanLimitBanner] Upgrade solicitado");
  };

  const handleAdjustFile = () => {
    if (typeof onAdjustFileClick === "function") {
      onAdjustFileClick(planLimit);
      return;
    }
    console.info("[PlanLimitBanner] Ajuste de arquivo solicitado", planLimit);
  };

  const projected = planLimit.projectedActiveStudentsAfterImport;
  const delta = planLimit.activeStudentsDeltaFromImport;
  const excess = typeof planLimit.excessOverLimit === "number"
    ? planLimit.excessOverLimit
    : (projected != null ? Math.max(0, projected - max) : Math.max(0, current - max));

  const title = limitExceeded
    ? (projected != null
        ? "Quase lá! Este arquivo ultrapassa o limite do seu plano"
        : "Sua rede atingiu o limite de alunos do plano")
    : "Plano contratado";

  const message = limitExceeded ? (
    projected != null ? (
      <>
        Com este arquivo, a rede ficaria com <strong>{formatNumber(projected)}</strong>{" "}
        {pluralize(projected, "aluno ativo", "alunos ativos")} — <strong>{formatNumber(excess)}</strong>{" "}
        {pluralize(excess, "a mais", "a mais")} do que o plano <strong>{planName}</strong> comporta.
        {hasUpgrade ? (
          <>
            {" "}Para concluir a importação, você pode fazer um upgrade de plano ou remover/inativar pelo
            menos <strong>{formatNumber(excess)}</strong> {pluralize(excess, "aluno", "alunos")} no arquivo.
          </>
        ) : (
          <>
            {" "}Você já está no plano mais alto. Para concluir, remova/inative pelo menos{" "}
            <strong>{formatNumber(excess)}</strong> {pluralize(excess, "aluno", "alunos")} no arquivo ou
            fale com o nosso time comercial.
          </>
        )}
      </>
    ) : (
      <>
        A rede {mantenedoraName ? <strong>{mantenedoraName}</strong> : "da sua mantenedora"} já tem{" "}
        <strong>{formatNumber(current)}</strong> {pluralize(current, "aluno ativo", "alunos ativos")} —{" "}
        <strong>{formatNumber(excess)}</strong> acima do limite de{" "}
        <strong>{formatNumber(max)}</strong> do plano <strong>{planName}</strong>.
        {hasUpgrade ? (
          <>
            {" "}Para regularizar, faça upgrade para um dos planos disponíveis:{" "}
            {optionsList}; ou reduza pelo menos <strong>{formatNumber(excess)}</strong>{" "}
            {pluralize(excess, "aluno ativo", "alunos ativos")} da rede.
          </>
        ) : (
          <>
            {" "}Você já está no plano mais alto disponível. Reduza pelo menos{" "}
            <strong>{formatNumber(excess)}</strong> {pluralize(excess, "aluno ativo", "alunos ativos")}{" "}
            da rede ou entre em contato com o nosso time comercial.
          </>
        )}
      </>
    )
  ) : (
    <>
      Plano <strong>{planName}</strong>
      {mantenedoraName && (
        <>
          {" "}da rede <strong>{mantenedoraName}</strong>
        </>
      )}
      .
    </>
  );

  return (
    <div className={`${styles.banner} ${limitExceeded ? styles.bannerExceeded : styles.bannerOk}`}>
      <div className={styles.icon} aria-hidden="true">
        {limitExceeded ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" fill="#dc3545" />
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 5h2v6h-2V7zm0 8h2v2h-2v-2z" fill="#0962ad" />
          </svg>
        )}
      </div>

      <div className={styles.content}>
        <h3 className={`${styles.title} ${limitExceeded ? styles.titleExceeded : styles.titleOk}`}>
          {title}
        </h3>
        <p className={styles.message}>{message}</p>

        <div className={styles.metrics}>
          <span className={styles.metric}>
            Uso atual da rede: <strong>{formatNumber(current)}</strong> / {formatNumber(max)}
          </span>
          {projected != null && (
            <span className={styles.metric}>
              Projeção com este arquivo: <strong>{formatNumber(projected)}</strong> ativos
            </span>
          )}
          {delta != null && typeof delta === "number" && (
            <span className={styles.metric}>
              Variação estimada: <strong>{delta >= 0 ? `+${delta}` : String(delta)}</strong>
            </span>
          )}
        </div>

        {limitExceeded && (hasUpgrade || projected != null) && (
          <div className={styles.actions}>
            {hasUpgrade && (
              <button
                type="button"
                onClick={handleUpgrade}
                className={`${styles.upgradeButton} ${styles.upgradeButtonExceeded}`}
              >
                Fazer upgrade
              </button>
            )}
            {projected != null && (
              <button
                type="button"
                onClick={handleAdjustFile}
                className={styles.adjustFileButton}
              >
                Ajustar arquivo
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanLimitBanner;
